import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import Spinner from '../components/Spinner';
import {PlusIcon, TrashIcon} from '../components/icons';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {createTrip, fetchTrip, TripEvent, TripRequest, updateTrip} from '../service/TripApi';

interface ApiErrorLike {
    response?: {status?: number; data?: {error?: string; description?: string}};
}

const SAVE_ERROR_FALLBACK = 'Не удалось сохранить поездку. Попробуйте ещё раз.';

/** Текст ошибки сохранения: понятное описание backend, иначе общий fallback. */
export const tripSaveErrorText = (err: unknown): string => {
    const data = (err as ApiErrorLike | undefined)?.response?.data;
    return data?.description?.trim() || data?.error?.trim() || SAVE_ERROR_FALLBACK;
};

/** Клиентская проверка дат — та же, что на сервере: ошибка видна до отправки. */
export const validateTripDates = (startDate: string, endDate: string): string | null => {
    if (!startDate || !endDate) {
        return 'Укажите даты начала и окончания';
    }
    return endDate < startDate ? 'Дата окончания не может быть раньше даты начала' : null;
};

const TripFormPage: React.FC = () => {
    const navigate = useNavigate();
    const {id} = useParams<{id: string}>();
    const editing = Boolean(id);

    const [name, setName] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [locationCode, setLocationCode] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [notes, setNotes] = useState('');
    const [events, setEvents] = useState<TripEvent[]>([]);
    const [loading, setLoading] = useState(editing);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useDocumentTitle();

    const load = useCallback(async () => {
        if (!id) {
            return;
        }
        setLoading(true);
        try {
            const trip = await fetchTrip(id);
            setName(trip.name);
            setCity(trip.city ?? '');
            setCountry(trip.country ?? '');
            setLocationCode(trip.locationCode ?? '');
            setStartDate(trip.startDate);
            setEndDate(trip.endDate);
            setNotes(trip.notes ?? '');
            setEvents(trip.events ?? []);
            setError(null);
        } catch (e) {
            setError('Не удалось загрузить поездку.');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const addEvent = () => setEvents(prev => [...prev, {eventDate: startDate || '', title: ''}]);

    const updateEvent = (index: number, patch: Partial<TripEvent>) =>
        setEvents(prev => prev.map((e, i) => (i === index ? {...e, ...patch} : e)));

    const removeEvent = (index: number) => setEvents(prev => prev.filter((_, i) => i !== index));

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        const dateError = validateTripDates(startDate, endDate);
        if (!name.trim()) {
            setError('Укажите название поездки');
            return;
        }
        if (dateError) {
            setError(dateError);
            return;
        }
        setSaving(true);
        setError(null);
        const request: TripRequest = {
            name: name.trim(),
            city: city.trim() || null,
            country: country.trim() || null,
            locationCode: locationCode.trim() || null,
            startDate,
            endDate,
            notes: notes.trim() || null,
            events: events.filter(ev => ev.title.trim() && ev.eventDate),
        };
        try {
            const saved = editing && id ? await updateTrip(id, request) : await createTrip(request);
            navigate(`/trips/${saved.id}`);
        } catch (err) {
            setError(tripSaveErrorText(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12" data-testid="trip-form-loading">
                <Spinner/>
            </div>
        );
    }

    return (
        <form className="space-y-6 max-w-2xl" onSubmit={submit} data-testid="trip-form">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {editing ? 'Редактирование поездки' : 'Новая поездка'}
            </h1>

            {error && (
                <div role="alert" data-testid="trip-form-error"
                     className="p-3 rounded-lg border border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300">
                    {error}
                </div>
            )}

            <div className="space-y-4">
                <div>
                    <label htmlFor="trip-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Название
                    </label>
                    <input id="trip-name" data-testid="trip-name" type="text" value={name} required
                           onChange={e => setName(e.target.value)}
                           className="w-full min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"/>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <label htmlFor="trip-start" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Начало
                        </label>
                        <input id="trip-start" data-testid="trip-start" type="date" value={startDate} required
                               onChange={e => setStartDate(e.target.value)}
                               className="w-full min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"/>
                    </div>
                    <div>
                        <label htmlFor="trip-end" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Окончание
                        </label>
                        <input id="trip-end" data-testid="trip-end" type="date" value={endDate} required
                               onChange={e => setEndDate(e.target.value)}
                               className="w-full min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"/>
                    </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <label htmlFor="trip-city" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Город
                        </label>
                        <input id="trip-city" data-testid="trip-city" type="text" value={city}
                               onChange={e => setCity(e.target.value)}
                               className="w-full min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"/>
                    </div>
                    <div>
                        <label htmlFor="trip-country" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Страна
                        </label>
                        <input id="trip-country" data-testid="trip-country" type="text" value={country}
                               onChange={e => setCountry(e.target.value)}
                               className="w-full min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"/>
                    </div>
                </div>

                <div>
                    <label htmlFor="trip-location-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Код локации для погоды
                    </label>
                    <input id="trip-location-code" data-testid="trip-location-code" type="text" value={locationCode}
                           placeholder="Например, BCN или Барселона"
                           onChange={e => setLocationCode(e.target.value)}
                           className="w-full min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"/>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        По нему берётся погода места назначения. Пусто — используем город поездки.
                    </p>
                </div>

                <div>
                    <label htmlFor="trip-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Заметки
                    </label>
                    <textarea id="trip-notes" data-testid="trip-notes" value={notes} rows={3}
                              onChange={e => setNotes(e.target.value)}
                              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"/>
                </div>
            </div>

            <section className="space-y-3">
                <div className="flex items-center justify-between gap-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">События</h2>
                    <button type="button" onClick={addEvent} data-testid="trip-event-add"
                            className="flex items-center gap-2 text-sm min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600">
                        <PlusIcon className="w-4 h-4"/>
                        Добавить
                    </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    События задают повод дня: образ на конференцию не должен совпадать с пляжным.
                </p>
                {events.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="trip-events-empty">
                        Событий нет — образы соберутся по погоде и рейтингу вещей.
                    </p>
                ) : (
                    <ul className="space-y-2">
                        {events.map((event, index) => (
                            <li key={`${event.eventDate}-${index}`} className="flex flex-col sm:flex-row gap-2">
                                <input type="date" value={event.eventDate} aria-label="Дата события"
                                       data-testid={`trip-event-date-${index}`}
                                       onChange={e => updateEvent(index, {eventDate: e.target.value})}
                                       className="min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"/>
                                <input type="text" value={event.title} placeholder="Название события"
                                       aria-label="Название события"
                                       data-testid={`trip-event-title-${index}`}
                                       onChange={e => updateEvent(index, {title: e.target.value})}
                                       className="flex-1 min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"/>
                                <button type="button" onClick={() => removeEvent(index)}
                                        aria-label="Удалить событие"
                                        data-testid={`trip-event-remove-${index}`}
                                        className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400">
                                    <TrashIcon className="w-5 h-5"/>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <div className="flex gap-3">
                <button type="submit" disabled={saving} data-testid="trip-submit"
                        className="btn-primary min-h-[44px]">
                    {saving ? 'Сохранение…' : 'Сохранить'}
                </button>
                <button type="button" onClick={() => navigate(-1)} data-testid="trip-cancel"
                        className="min-h-[44px] px-4 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">
                    Отмена
                </button>
            </div>
        </form>
    );
};

export default TripFormPage;
